import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, {
  setAccessToken,
  setUnauthorizedHandler,
  setRefreshHandler,
} from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const setSession = useCallback((nextUser, nextAccess) => {
    setAccessToken(nextAccess || null);
    setUser(nextUser || null);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await api.post('/api/auth/refresh');
    setSession(data.user, data.access);
    return data;
  }, [setSession]);

  useEffect(() => {
    setRefreshHandler(async () => {
      await refresh();
    });
    setUnauthorizedHandler(() => {
      setSession(null, null);
    });
  }, [refresh, setSession]);

  useEffect(() => {
    let cancelled = false;
    // Встраивание в minibrowser Trassir: авторизация по токену (нет сессии/cookie).
    // Единые ссылки + разовый вход на АРМ:
    //   ?setup=<токен> — один раз на каждом АРМ: запоминаем токен учётки в браузере.
    //   ?token=<токен> — разовый явный токен в ссылке (приоритетнее, как было).
    //   иначе в embed — берём запомненный токен из localStorage этого АРМ.
    const params = new URLSearchParams(window.location.search);
    const isEmbed = params.get('embed') === '1';
    const setupToken = params.get('setup');
    let embedToken = params.get('token');
    if (setupToken) {
      try { localStorage.setItem('embed_token', setupToken); } catch (_) {}
      embedToken = setupToken;
      // Убираем токен из адресной строки (чтобы не светился и перезагрузка брала из localStorage).
      try { window.history.replaceState({}, '', window.location.pathname + (isEmbed ? '?embed=1' : '')); } catch (_) {}
    }
    if (!embedToken && isEmbed) {
      try { embedToken = localStorage.getItem('embed_token') || null; } catch (_) {}
    }
    if (embedToken) {
      let u = { id: 0, role: 'operator', email: 'embed' };
      try {
        const p = JSON.parse(atob(embedToken.split('.')[1]));
        u = { id: p.sub, role: p.role || 'operator', email: p.email || 'embed' };
      } catch (_) {}
      setSession(u, embedToken);
      setIsLoading(false);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data } = await api.post('/api/auth/refresh');
        if (!cancelled) setSession(data.user, data.access);
      } catch (_) {
        if (!cancelled) setSession(null, null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setSession]);

  const login = async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password });
    setSession(data.user, data.access);
    return data;
  };

  // Вход встроенной карты (АРМ) по логину/паролю сервисной учётки. Токен кладём в
  // localStorage этого АРМ; дальше обычные ссылки на машины работают без логина.
  const embedLogin = useCallback(async (email, password) => {
    const { data } = await api.post('/api/auth/embed-login', { email, password });
    try { localStorage.setItem('embed_token', data.token); } catch (_) {}
    let u = { id: 0, role: 'operator', email: 'embed' };
    try {
      const p = JSON.parse(atob(data.token.split('.')[1]));
      u = { id: p.sub, role: p.role || 'operator', email: p.email || 'embed' };
    } catch (_) {}
    setSession(u, data.token);
    return data;
  }, [setSession]);

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (_) {}
    setSession(null, null);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    embedLogin,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
